/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/require-default-props */
import React, { forwardRef } from 'react';

import PropTypes from 'prop-types';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { Label } from 'uikit/labels';
import { Typo } from 'uikit/content';
import { CardMedia, CardSkeleton } from 'uikit/cards';

import styles from '../styles/uikitCardsCard.module.scss';

const Card = forwardRef(
  (
    {
      children = null,
      type = '',
      variant = 'social',
      image = null,
      status = '',
      duration = 0,
      progress = 0,
      dateMessage = '',
      isComplete = false,
      liveText = '',
      isExternalLink = false,
      to = '',
      isSkeletonLoading = false,
      element = 'div',
      id = '',
      className = '',
      onClick = null,
      testId = '',
      trackingId = '',
      videoType = '', // deprecated in favor of type
    },
    ref,
  ) => {
    const cardProps = {};
    let Element = element;

    if (to && isExternalLink) {
      Element = 'a';
      cardProps.href = to;
    } else if (to) {
      Element = Link;
      cardProps.to = to;
    } else if (onClick) {
      Element = 'button';
      cardProps.type = 'button';
      cardProps.onClick = onClick;
    }

    // To handle any continue usage of deprecated videoType prop
    // Otherwise, we can just drop this and use type
    const usageType = type || videoType;

    return (
      <Element
        className={classnames(
          styles.element,
          {
            [styles.hasHover]: !!to || !!onClick,
            [styles.isGridContainer]: variant === 'grid',
          },
          className,
        )}
        ref={ref}
        id={id}
        data-testid={testId}
        data-object-name={trackingId}
        {...cardProps}
      >
        {variant === 'social' && children}

        {variant === 'grid' && (
          <div className={styles.isVariantGrid}>
            {isSkeletonLoading ? (
              <CardSkeleton variant="grid" />
            ) : (
              <motion.div
                className={styles.motion}
                key={id || 'key'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CardMedia
                  variant="grid"
                  type={usageType}
                  image={image}
                  status={status}
                  progress={progress}
                  duration={duration}
                  isComplete={isComplete}
                  liveText={liveText}
                  dateMessage={dateMessage}
                  folderIconClassName={styles.folder}
                />
                {children}
              </motion.div>
            )}
          </div>
        )}
        {variant === 'list' &&
          (isSkeletonLoading ? (
            <CardSkeleton variant="list" />
          ) : (
            <motion.div
              className={styles.motion}
              key={id || 'key'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className={styles.isVariantList}>
                <CardMedia
                  type={usageType}
                  variant="list"
                  image={image}
                  status={status}
                  progress={progress}
                  duration={duration}
                  isComplete={isComplete}
                  folderIconClassName={styles.folder}
                />
                {children}
              </div>
            </motion.div>
          ))}
      </Element>
    );
  },
);

const ContentContainer = ({
  children,
  variant,
  dateMessage = '',
  dateMessageType = 'default',
  liveText = '',
  testId = '',
}) => (
  <div className={styles.contentContainer} data-testid={testId}>
    {liveText && variant === 'list' && (
      <Label text={liveText} type="live" className={styles.topLabel} />
    )}
    {!liveText && dateMessage && variant === 'list' && (
      <Label text={dateMessage} type={dateMessageType} className={styles.topLabel} />
    )}
    {children}
  </div>
);

const Content = ({ title, description = '', variant = '' }) => (
  <div className={styles.content}>
    <div className={styles.title}>
      <Typo element="h3" variant="h5">
        {title}
      </Typo>
    </div>
    {variant === 'list' && (
      <div className={styles.description}>
        <Typo element="p" variant="b01">
          {description}
        </Typo>
      </div>
    )}
  </div>
);

const MetaContainer = ({ children }) => <div className={styles.metaContainer}>{children}</div>;

Card.propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(['course', 'live', 'topic', 'video', 'upnext']),
  variant: PropTypes.oneOf(['grid', 'list', 'social']),
  className: PropTypes.string,
  image: PropTypes.string,
  status: PropTypes.oneOf([
    'completed',
    'equivalentPassed',
    'facilitated',
    'inProgress',
    'overdue',
    'recommended',
    'required',
    '',
  ]),
  duration: PropTypes.number,
  progress: PropTypes.number,
  dateMessage: PropTypes.string,
  isComplete: PropTypes.bool,
  liveText: PropTypes.string,
  isExternalLink: PropTypes.bool,
  to: PropTypes.string,
  isSkeletonLoading: PropTypes.bool,
  element: PropTypes.oneOf(['div', 'article']),
  id: PropTypes.string,
  onClick: PropTypes.func,
  testId: PropTypes.string,
  trackingId: PropTypes.string,
  videoType: PropTypes.oneOf(['live', 'topic', 'video']),
};

ContentContainer.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['grid', 'list']).isRequired,
  dateMessage: PropTypes.string,
  dateMessageType: PropTypes.oneOf(['default', 'success', 'warning']),
  liveText: PropTypes.string,
  testId: PropTypes.string,
};

Content.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  variant: PropTypes.oneOf(['', 'grid', 'list']),
};

MetaContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

Card.ContentContainer = ContentContainer;
Card.Content = Content;
Card.MetaContainer = MetaContainer;

export { Card as CardStorybook };
export default Card;
